import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiRefreshCw,
  FiShield,
} from "react-icons/fi";
import { ensurePayoutsAccessToken, payoutApi } from "./api/payoutApiClient";
import {
  clearTransferSession,
  compactBody,
  errorMessage,
  isPayoutMockMode,
  isPayoutStepUpEnabled,
  pickString,
  readSessionJson,
  rupeesToPaise,
  stableIdempotencyKey,
  verifyStepUp,
  writeSessionJson,
} from "./api/payoutMutationHelpers";
import { validateOtpCode } from "./api/payoutValidation";
import { showPayoutNotification } from "./PayoutNotification";

type TransferDraft = {
  account?: Record<string, unknown>;
  beneficiary: Record<string, unknown> & { title: string };
  purpose?: string;
  category?: string;
  method: string;
  schedule?: string;
  scheduleDate?: string;
  scheduleTime?: string;
  scheduledFor: string;
  amount: number;
  remarks?: string;
  uploadedFile?: { name: string; size: number } | null;
};

const defaultDraft: TransferDraft = {
  beneficiary: {
    title: "-",
  },
  method: "-",
  scheduledFor: "-",
  amount: 0,
};

const formatINR = (amount: number) =>
  `\u20B9 ${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const readTransferDraft = () => {
  try {
    const storedDraft = sessionStorage.getItem("payoutTransferDraft");
    return storedDraft ? (JSON.parse(storedDraft) as TransferDraft) : defaultDraft;
  } catch {
    return defaultDraft;
  }
};

function FlowStep({
  index,
  label,
  active,
  complete,
}: {
  index: number;
  label: string;
  active?: boolean;
  complete?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={
          "flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold " +
          (complete
            ? "bg-[#1B163B] text-white"
            : active
              ? "bg-[#1B163B] text-white"
              : "border border-[#D6DCEB] bg-white text-[#505E8E]")
        }
      >
        {complete ? <FiCheck /> : index}
      </span>
      <span
        className={
          "text-sm font-semibold " +
          (active ? "text-[#1B163B]" : "text-[#505E8E]")
        }
      >
        {label}
      </span>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-6 border-b border-[#E0E5F2] py-3 last:border-b-0">
      <span className="text-sm font-medium text-[#505E8E]">{label}</span>
      <div className="max-w-[220px] text-right text-sm font-semibold text-[#1B163B]">
        {value}
      </div>
    </div>
  );
}

function PayoutVerifyTransfer() {
  const navigate = useNavigate();
  const draft = useMemo(readTransferDraft, []);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const method = "authenticator" as const;
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const code = useMemo(() => otp.join(""), [otp]);
  const stepUpRequired = isPayoutStepUpEnabled();
  const ready = !validateOtpCode(code);
  const [submitting, setSubmitting] = useState(false);
  const [, setSubmitError] = useState("");
  const showSubmitError = React.useCallback((message: string) => {
    setSubmitError(message);
    showPayoutNotification({ tone: "error", message });
  }, []);
  const verificationInstruction = stepUpRequired
    ? "Enter the 6-digit code from your Google Authenticator app."
    : "Verify transfer: enter the 6-digit verification code to continue.";

  const updateDigit = (index: number, value: string) => {
    const nextValue = value.replace(/\D/g, "").slice(-1);
    const nextOtp = [...otp];
    nextOtp[index] = nextValue;
    setOtp(nextOtp);

    if (nextValue && index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 6).split("");
    if (!digits.length) return;
    const nextOtp = ["", "", "", "", "", ""];
    digits.forEach((digit, index) => {
      nextOtp[index] = digit;
    });
    setOtp(nextOtp);
    inputsRef.current[Math.min(digits.length, 5)]?.focus();
  };

  const handleVerifyAndConfirm = async () => {
    setSubmitError("");
    const otpError = validateOtpCode(
      code,
      "Verify transfer: enter the 6-digit Google Authenticator code to create this payout.",
    );
    if (otpError) {
      showSubmitError(otpError);
      return;
    }
    if (submitting) return;

    if (isPayoutMockMode()) {
      showSubmitError(
        "Mock mode is active — no payment is sent to the API. Set REACT_APP_PAYOUTS_DATA_SOURCE=api and restart npm start.",
      );
      return;
    }

    setSubmitting(true);
    try {
      await ensurePayoutsAccessToken();
      const beneficiaryId = pickString(
        draft.beneficiary,
        ["beneficiary_id", "beneficiaryId", "id", "value"],
      );
      const fundingAccountId = pickString(
        draft.account,
        ["funding_account_id", "fundingAccountId", "id", "accountId", "value"],
      );
      if (!beneficiaryId || !fundingAccountId) {
        throw new Error(
          "Missing payout account or beneficiary IDs. Go back to Initiate Transfer and wait for API data to load.",
        );
      }
      const clientReferenceId =
        readSessionJson<string | null>("payoutTransferClientReferenceId", null);
      if (!clientReferenceId) {
        throw new Error("Transfer session expired. Start again from Initiate Transfer.");
      }
      const stepUpToken = stepUpRequired
        ? await verifyStepUp({
            purpose: "CREATE_PAYOUT",
            method,
            code,
            subjectId: beneficiaryId,
          })
        : undefined;
      const body = compactBody({
        funding_account_id: fundingAccountId,
        beneficiary_id: beneficiaryId,
        amount_paise: rupeesToPaise(draft.amount),
        mode: draft.method,
        client_reference_id: clientReferenceId,
      });
      const response = await payoutApi.createPayment(
        body,
        stableIdempotencyKey("create-payment", clientReferenceId),
        stepUpToken,
      );
      writeSessionJson("payoutTransferCreateResult", response);
      navigate("/payouts/transfers/initiate/confirm");
    } catch (error) {
      showSubmitError(errorMessage(error, "Verify transfer: unable to create this payout transfer. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
          Two-Step Verification
        </h1>
        <p className="mt-1 text-sm font-medium text-[#505E8E]">
          Verify this payout transfer before it is submitted for approval.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-8">
        <FlowStep index={1} label="Enter Details" complete />
        <span className="h-px w-16 bg-[#D6DCEB]" />
        <FlowStep index={2} label="Review" complete />
        <span className="h-px w-16 bg-[#D6DCEB]" />
        <FlowStep index={3} label="Verify" active />
        <span className="h-px w-16 bg-[#D6DCEB]" />
        <FlowStep index={4} label="Confirm" />
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,760px)_380px]">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-6 flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] bg-[#1B163B0D] text-xl text-[#1B163B]">
              <FiShield />
            </div>
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                Verify Transfer Request
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                {verificationInstruction}
              </p>
            </div>
          </div>

          <div>
            <div className="rounded-[8px] border border-[#1B163B] bg-[#1B163B0D] px-5 py-4 text-left">
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-[6px] bg-[#DCFCE7] text-[#16A34A]">
                    <FiShield />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-[#1B163B]">
                      Google Authenticator
                    </span>
                    <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                      Recommended for high-value payouts
                    </span>
                  </span>
                </span>
                <FiCheck className="text-lg text-[#16A34A]" />
              </div>
            </div>
          </div>

          <div className="mt-8">
            <label className="mb-3 block text-sm font-semibold text-[#1B163B]">
              Verification Code
            </label>
            <div className="flex flex-wrap gap-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(element) => {
                    inputsRef.current[index] = element;
                  }}
                  value={digit}
                  onChange={(event) => updateDigit(index, event.target.value)}
                  onPaste={(event) => {
                    event.preventDefault();
                    handlePaste(event.clipboardData.getData("text"));
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Backspace" && !otp[index] && index > 0) {
                      inputsRef.current[index - 1]?.focus();
                    }
                  }}
                  inputMode="numeric"
                  className="h-12 w-12 rounded-[6px] border border-[#E0E5F2] bg-white text-center text-lg font-semibold text-[#1B163B] outline-none focus:border-[#1B163B]"
                />
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-5 text-sm font-semibold">
              <button
                type="button"
                onClick={() => setOtp(["", "", "", "", "", ""])}
                className="flex items-center gap-2 text-[#1B163B]"
              >
                <FiRefreshCw />
                Resend code
              </button>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/payouts/transfers/initiate/review"
                className="flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
              >
                <FiArrowLeft />
                Back
              </Link>
              <Link
                to="/payouts/transfers"
                onClick={clearTransferSession}
                className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
              >
                Cancel
              </Link>
            </div>
            <button
              type="button"
              onClick={handleVerifyAndConfirm}
              disabled={!ready || submitting}
              style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
              className={
                "flex h-10 min-w-[170px] items-center justify-center gap-3 rounded-[6px] px-7 text-sm font-semibold text-white " +
                (ready && !submitting
                  ? ""
                  : "opacity-50")
              }
            >
              {submitting
                ? "Submitting..."
                : "Verify & Continue"}
              <FiArrowRight />
            </button>
          </div>
        </section>

        <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="text-[18px] font-semibold text-[#1B163B]">
            Verification Summary
          </h2>
          <div className="my-5 h-px bg-[#E0E5F2]" />
          <SummaryRow label="Transfer Amount" value={formatINR(draft.amount)} />
          <SummaryRow label="Beneficiary" value={draft.beneficiary.title} />
          <SummaryRow label="Transfer Type" value={draft.method} />
          <SummaryRow
            label="Scheduled For"
            value={draft.scheduledFor === "Now" ? "Immediate" : draft.scheduledFor}
          />
          <SummaryRow label="Verification" value="Google Authenticator" />
          <div className="mt-5 rounded-[6px] bg-[#DCFCE7] px-4 py-3 text-sm font-medium text-[#166534]">
            Your transfer details are locked for review while verification is in
            progress.
          </div>
        </aside>
      </div>
    </div>
  );
}

export default PayoutVerifyTransfer;
